import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Header } from '@shared/ui/header';
import { LgAg26Footer } from '@shared/ui/unlock-vuz-banner';
import { useUTMSource } from '@shared/hooks/useUTMSource';
import { windowOpenBlank } from '@shared/utils';

import checkIcon from '@shared/assets/lg_ag26/confirm_check.svg';
import stepDownload from '@shared/assets/lg_ag26/confirm_step_download.svg';
import stepSignin from '@shared/assets/lg_ag26/confirm_step_signin.svg';
import stepPlay from '@shared/assets/lg_ag26/confirm_step_play.svg';
import androidIcon from '@shared/assets/lg_ag26/confirm_android.svg';
import appleIcon from '@shared/assets/lg_ag26/confirm_apple.svg';
import googlePlayLogo from '@shared/assets/lg_ag26/store_google_play.png';
import appStoreLogo from '@shared/assets/lg_ag26/store_app_store.png';

import styles from '@features/thank-you/styles/ThankyouLgAg26.module.css';
import '@shared/ui/unlock-vuz-banner/styles/LgAg26Landing.css';

const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.creativeinnovations.mea&hl=en';
const APP_STORE_URL = 'https://apps.apple.com/us/app/360-vuz-live-vr-video-views/id964118383';

const STEPS = [
  {
    icon: stepDownload,
    titleKey: 'lg_ag26_step1_title',
    textKey: 'lg_ag26_step1_text',
    textKeyMobile: 'lg_ag26_step1_text_mobile',
  },
  {
    icon: stepSignin,
    titleKey: 'lg_ag26_step2_title',
    textKey: 'lg_ag26_step2_text',
    textKeyMobile: 'lg_ag26_step2_text_mobile',
  },
  {
    icon: stepPlay,
    titleKey: 'lg_ag26_step3_title',
    textKey: 'lg_ag26_step3_text',
  },
];

export const ThankyouLgAg26 = ({
  activationURL,
  languageConfig = {},
  showOpenButton = false,
}) => {
  const { t } = useTranslation();
  const UTMSourceURL = useUTMSource();
  const shouldShowButton = showOpenButton ? Boolean(activationURL) : true;

  const handleRedirect = () => {
    windowOpenBlank(activationURL, UTMSourceURL);
  };

  const playHref = activationURL || PLAY_STORE_URL;
  const appleHref = activationURL || APP_STORE_URL;

  return (
    <div className="lg_ag26-thankyou">
      <Header languageConfig={languageConfig} isWhite />

      <div className={styles.page}>
        <div className={styles.content}>
          <div className={styles.success}>
            <div className={styles.check} aria-hidden>
              <img src={checkIcon} alt="" />
            </div>
            <h1 className={styles.title}>
              <span className={styles.titleDesktop}>{t('lg_ag26_subscribed')}</span>
              <span className={styles.titleMobile}>{t('lg_ag26_subscribed_mobile')}</span>
            </h1>
            <p className={styles.welcome}>
              <Trans
                i18nKey="lg_ag26_welcome_vip"
                components={{ vip: <span className={styles.vip} dir="ltr" /> }}
              />
            </p>
          </div>

          <div className={styles.card}>
            <div className={styles.storesBlock}>
              <h2 className={styles.downloadTitle}>{t('lg_ag26_download_the_app')}</h2>

              <div className={styles.storeButtons}>
                <a
                  className={styles.storeBtn}
                  href={playHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img className={styles.storeLogo} src={googlePlayLogo} alt="" />
                  <span className={styles.storeCopy}>
                    <span className={styles.storeCaption}>GET IT ON</span>
                    <span className={styles.storeName}>Google Play</span>
                  </span>
                </a>

                <a
                  className={styles.storeBtn}
                  href={appleHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img className={`${styles.storeLogo} ${styles.storeLogoApple}`} src={appStoreLogo} alt="" />
                  <span className={styles.storeCopy}>
                    <span className={styles.storeCaption}>Download on the</span>
                    <span className={styles.storeName}>App Store</span>
                  </span>
                </a>
              </div>
            </div>

            <div className={styles.orRow}>
              <span>{t('lg_ag26_or')}</span>
            </div>

            <h2 className={styles.cardTitle}>{t('lg_ag26_how_to_get_app')}</h2>

            <div className={styles.steps}>
              <div className={styles.rail} aria-hidden>
                {STEPS.map((_, index) => (
                  <React.Fragment key={index}>
                    <div className={styles.stepNum}>{index + 1}</div>
                    {index < STEPS.length - 1 ? <div className={styles.stepLine} /> : null}
                  </React.Fragment>
                ))}
              </div>

              <ol className={styles.stepList}>
                {STEPS.map((step) => (
                  <li key={step.titleKey} className={styles.step}>
                    <div className={styles.stepIcon}>
                      <img src={step.icon} alt="" />
                    </div>
                    <div className={styles.stepCopy}>
                      <p className={styles.stepTitle}>{t(step.titleKey)}</p>
                      <p className={styles.stepTextDesktop}>{t(step.textKey)}</p>
                      <p className={styles.stepTextMobile}>
                        {t(step.textKeyMobile || step.textKey)}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {shouldShowButton ? (
            <div className={styles.cta}>
              <button type="button" className={styles.ctaButton} onClick={handleRedirect}>
                <img className={styles.ctaIcon} src={stepDownload} alt="" />
                <span>{t('lg_ag26_download_app')}</span>
              </button>
              <div className={styles.stores}>
                <span>{t('lg_ag26_available_android')}</span>
                <img src={androidIcon} alt="" />
                <span>{t('lg_ag26_available_ios')}</span>
                <img src={appleIcon} alt="" />
              </div>
            </div>
          ) : null}
        </div>

        <LgAg26Footer />
      </div>
    </div>
  );
};
